import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ILoadingQuestionImg } from '../../../@types/tabs';
import {
  showErrorMessage,
  showSuccessMessage,
} from './../../../../helpers/showMessage';
import { ConductSkeleton } from './../../MyAuditSkeleton/ConductSkeleton';
import {
  deleteMyAuditAttachment,
  getMyAudit,
  saveMyAudit,
  saveMyAuditAttachment,
  submitMyAudit,
} from '../../../../requests/myAudit';
import { ButtonBlockColumn } from './../../../common/sharedStyledComponents/GenericStyles.styled';
import {
  ModalContent,
  ModalHeader,
  MyAuditConductButtonContainer,
  ScoreContainer,
  StyledMyAuditConductContainer,
} from './styled.MyAuditConduct';
import { TabsWrapper } from '../MyAuditView/styled.MyAuditView';
import { ButtonLarge } from '../../../common/sharedComponents/buttons/ButtonLarge';
import { convertPayloadToMyAudit, validateMyAuditDateRange } from './utils';
import { sentryReport } from '../../../../helpers/sentry';
import { Modal } from '../../../common/sharedComponents/Modal';
import { Body4, Headline3 } from '../../../common/typography';
import { MyAuditConductModalSkeleton } from '../../MyAuditSkeleton/ConductSkeleton/MyAuditConductModalSkeleton';
import { AppHeader } from '../../../common/sharedComponents/AppHeader';
import { HeaderContainer } from '../../../common/sharedStyledComponents/MyAuditGenericStyles.styled';
import { ScoreTypes } from '../../../@types/genericTypes';
import { Score } from '../../../myAudit/components/Score';
import { IScoreValues } from '../../../@types/pill';

import {
  IAttachment,
  IMyAudit,
  IStandard,
  MealPeriodTypes,
} from '../../../@types/myAudit';
import { ISelectedOption } from '../../../@types/searchBox';
import { IDropdownOption } from '../../../@types/dropdown';
import { MyAuditConductTabContent } from './MyAuditConductTabContent';
import { TabContainer } from '../../../common/sharedComponents/TabContainer';
import moment from 'moment';

export function MyAuditConduct () {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const auditId = Number(searchParams.get('auditId')) || 0;

  const [activeTabContent, setActiveTabContent] = useState<IMyAudit>();
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isShowConfirmationSubmitModal, setIsShowConfirmationSubmitModal] =
    useState<boolean>(false);
  const [standardsMissingGrading, setStandardsMissingGrading] =
    useState<IStandard[]>();
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const [isLoadingImg, setIsLoadingImg] = useState<boolean>(false);
  const [isLoadingImgDelete, setIsLoadingImgDelete] = useState<boolean>(false);
  const [questionImgLoading, setQuestionImgLoading] =
    useState<ILoadingQuestionImg>();
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | string | undefined>(
    undefined
  );
  const [dateTime, setDateTime] = useState<Date | string | undefined>(
    undefined
  );
  const [startDateRange, setStartDateRange] = useState<
    Date | string | undefined
  >(undefined);
  const [endDateRange, setEndDateRange] = useState<Date | string | undefined>(
    undefined
  );

  useEffect(() => {
    getMyAudit(auditId, 'conduct').then((response) => {
      setActiveTabContent(response.data);
      setIsLoading(false);

      response.data.myAuditSections.map((section) => {
        if (section.isDateTimeRequired) {
          switch (section.dateTimeFormat) {
            case 'date':
              setStartDate(section.fromDate);
              break;
            case 'dateTime':
              // we need to convert to utc again as new date will convert date to locale timezone
              setDateTime(
                section.fromDate &&
                  moment(section.fromDate).utc().format('YYYY-MM-DD HH:mm:ss')
              );
              break;
            case 'dateOnlyRange':
              setStartDateRange(section.fromDate);
              setEndDateRange(section.toDate);
              break;
          }
        }
      });
    });
  }, []);

  const scoreCountCalculator = (type: ScoreTypes) => {
    return (
      (activeTabContent &&
        activeTabContent.myAuditSections[selectedTabIndex].standards.filter(
          (standard) => standard.score === type
        ).length) ||
      0
    );
  };

  const totalScoreCalculator = () => {
    const metStandards = scoreCountCalculator(1);
    const noMetStandards = scoreCountCalculator(0);
    const totalValidStandards = metStandards + noMetStandards;
    const totalScore = (metStandards / totalValidStandards) * 100 || 0;
    return `${totalScore.toFixed(0)}%`;
  };

  const scoreList: IScoreValues[] = [
    {
      key: 'Score',
      value: totalScoreCalculator(),
      keyValue: 'Score',
      backgroundColor: 'platinum',
    },
    {
      key: 'Met',
      value: scoreCountCalculator(1),
      keyValue: 'Met',
      backgroundColor: 'verdigris',
    },
    {
      key: 'No',
      value: scoreCountCalculator(0),
      keyValue: 'No Met',
      backgroundColor: 'redCrayola',
    },
    {
      key: 'NA',
      value: scoreCountCalculator(null),
      keyValue: 'N/A',
      backgroundColor: 'battleShipGray',
    },
  ];

  function indexQuestionById (
    clonedActiveTabContent: IMyAudit,
    standardId: string | number
  ) {
    const section =
      clonedActiveTabContent &&
      clonedActiveTabContent.myAuditSections[selectedTabIndex];

    return (
      section &&
      section.standards.findIndex((standard) => standard.id === standardId)
    );
  }

  function handleScoreChanges (
    score: ScoreTypes | string,
    standardId: string | number
  ) {
    const updatedTabContent: IMyAudit = { ...activeTabContent! };
    const questionIndex: number = indexQuestionById(
      updatedTabContent,
      standardId!
    );
    if (updatedTabContent && questionIndex !== -1) {
      updatedTabContent.myAuditSections[selectedTabIndex].standards[
        questionIndex
      ].score = score;
    }
    setActiveTabContent(updatedTabContent);
  }

  function handleQuestionComment (
    textArea: string,
    standardId: string | number
  ) {
    const updatedTabContent: IMyAudit = { ...activeTabContent! };
    const questionIndex: number = indexQuestionById(
      updatedTabContent,
      standardId
    );
    if (updatedTabContent && questionIndex !== -1) {
      updatedTabContent.myAuditSections[selectedTabIndex].standards[
        questionIndex
      ].comments = textArea;
    }
    setActiveTabContent(updatedTabContent);
  }

  function handleMyAuditComments (textArea: string) {
    const updatedTabContent: IMyAudit = { ...activeTabContent! };
    if (updatedTabContent) {
      updatedTabContent.myAuditSections[selectedTabIndex].comments = textArea;
      updatedTabContent.myAuditSections[selectedTabIndex].comments = textArea;
    }
    setActiveTabContent(updatedTabContent);
  }

  function handleRoomNumber (textArea: string) {
    const updatedTabContent: IMyAudit = { ...activeTabContent! };
    if (updatedTabContent) {
      updatedTabContent.myAuditSections[selectedTabIndex].roomNumber = textArea;
    }
    setActiveTabContent(updatedTabContent);
  }

  function handleOutletName (textArea: string) {
    const updatedTabContent: IMyAudit = { ...activeTabContent! };
    if (updatedTabContent) {
      updatedTabContent.myAuditSections[selectedTabIndex].outletName = textArea;
    }
    setActiveTabContent(updatedTabContent);
  }

  function handleMealPeriod (selected: IDropdownOption) {
    const updatedTabContent: IMyAudit = { ...activeTabContent! };
    if (updatedTabContent) {
      updatedTabContent.myAuditSections[selectedTabIndex].mealPeriod =
        selected.value as MealPeriodTypes;
    }
    setActiveTabContent(updatedTabContent);
  }

  const addAttachmentOnAudit = (
    id: number,
    url: string,
    name: string,
    type: string,
    standardId?: string | number,
    questionPhoto?: boolean
  ) => {
    const updatedTabContent: IMyAudit = { ...activeTabContent! };
    const lqaImageFormat: IAttachment = { id, url, name, type };
    if (updatedTabContent && !questionPhoto) {
      updatedTabContent.myAuditSections[selectedTabIndex].attachments!.push(
        lqaImageFormat
      );
    } else if (updatedTabContent && questionPhoto && standardId) {
      const questionIndex: number = indexQuestionById(
        updatedTabContent,
        standardId
      );
      if (updatedTabContent && questionIndex !== -1) {
        updatedTabContent.myAuditSections[selectedTabIndex].standards[
          questionIndex
        ].attachment = lqaImageFormat;
      }
    }
    setActiveTabContent(updatedTabContent);
  };

  const deleteAttachmentOnAudit = (
    index: number,
    standardId?: string | number,
    questionPhoto?: boolean
  ) => {
    const updatedTabContent: IMyAudit = { ...activeTabContent! };
    if (updatedTabContent && !questionPhoto) {
      updatedTabContent.myAuditSections[selectedTabIndex].attachments =
        updatedTabContent.myAuditSections[selectedTabIndex].attachments!.filter(
          (_, i) => i !== index
        );
    } else if (updatedTabContent && questionPhoto && standardId) {
      const questionIndex: number = indexQuestionById(
        updatedTabContent,
        standardId
      );
      if (updatedTabContent && questionIndex !== -1) {
        delete updatedTabContent.myAuditSections[selectedTabIndex].standards[
          questionIndex
        ].attachment;
      }
    }

    setActiveTabContent(updatedTabContent);
  };

  function handleMediaUpload (
    newImage?: File | string,
    questionPhoto?: boolean,
    sectionId?: string | number,
    standardId?: string | number
  ) {
    questionPhoto
      ? setQuestionImgLoading({ id: standardId, isLoading: true })
      : setIsLoadingImg(true);
    const formData = new FormData();
    if (newImage) {
      formData.append('file', newImage);
    }
    saveMyAuditAttachment(formData, auditId, sectionId, standardId)
      .then((response) => {
        addAttachmentOnAudit(
          response.data.id,
          response.data.url,
          response.data.name,
          response.data.type,
          standardId,
          questionPhoto
        );
        showSuccessMessage(t('COMMON.ALERT_MESSAGES.UPLOADED_SUCCESSFULLY'));
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          alertMessage: t('COMMON.ALERT_MESSAGES.ATTACHMENT_ERROR'),
          method: 'handleMediaUpload/saveMyAuditAttachment',
          payload: {
            formData,
            auditId,
            sectionId,
            standardId,
          },
        });
        showErrorMessage(t('COMMON.ALERT_MESSAGES.ATTACHMENT_ERROR'));
      })
      .finally(() => {
        setIsLoadingImg(false);
        setQuestionImgLoading({ id: standardId, isLoading: false });
      });
  }

  const handleDeleteImage = (
    index?: number | undefined,
    fileId?: number | undefined,
    questionPhoto?: boolean | undefined,
    standardId?: string | number | undefined
  ) => {
    questionPhoto
      ? setQuestionImgLoading({ id: standardId, isLoading: true })
      : setIsLoadingImgDelete(true);
    deleteMyAuditAttachment(auditId, fileId)
      .then(() => {
        if (typeof index === 'number') {
          deleteAttachmentOnAudit(index, standardId, questionPhoto);
        }

        showSuccessMessage(t('COMMON.ALERT_MESSAGES.DELETED_SUCCESSFULLY'));
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          alertMessage: t('COMMON.ALERT_MESSAGES.UNABLE_TO_DELETE'),
          method: 'handleDeleteImage/deleteMyAuditAttachment',
          payload: {
            index,
            standardId,
            questionPhoto,
          },
        });
        showErrorMessage(t('COMMON.ALERT_MESSAGES.UNABLE_TO_DELETE'));
      })
      .finally(() => {
        setIsLoadingImgDelete(false);
        setQuestionImgLoading({ id: standardId, isLoading: false });
      });
  };

  const handleReplaceImage = (
    newImage: File | string,
    index: number,
    fileId: number,
    questionPhoto?: boolean,
    standardId?: string | number,
    sectionId?: string | number
  ) => {
    questionPhoto
      ? setQuestionImgLoading({ id: standardId, isLoading: true })
      : setIsLoadingImg(true);
    deleteMyAuditAttachment(auditId, fileId)
      .then(() => {
        deleteAttachmentOnAudit(index, standardId, questionPhoto);
        const formData = new FormData();
        formData.append('file', newImage);
        saveMyAuditAttachment(formData, auditId, sectionId, standardId)
          .then((response) => {
            addAttachmentOnAudit(
              response.data.id,
              response.data.url,
              response.data.name,
              response.data.type,
              standardId,
              questionPhoto
            );
            showSuccessMessage(
              t('COMMON.ALERT_MESSAGES.REPLACED_SUCCESSFULLY')
            );
          })
          .catch((error) => {
            sentryReport(error.response.data, error.message, error.code, {
              alertMessage: t('COMMON.ALERT_MESSAGES.UNABLE_TO_REPLACE'),
              method: 'handleReplaceImage/saveMyAuditAttachment',
              payload: {
                formData,
                auditId,
                sectionId,
                standardId,
              },
            });
            showErrorMessage(t('COMMON.ALERT_MESSAGES.UNABLE_TO_REPLACE'));
          })
          .finally(() => {
            setIsLoadingImg(false);
            setQuestionImgLoading({ id: standardId, isLoading: false });
          });
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          alertMessage: t('COMMON.ALERT_MESSAGES.UNABLE_TO_REPLACE'),
          method: 'handleReplaceImage/deleteMyAuditAttachment',
          payload: {
            auditId,
            fileId,
            index,
            standardId,
            questionPhoto,
          },
        });
        showErrorMessage(t('COMMON.ALERT_MESSAGES.UNABLE_TO_REPLACE'));
      });
  };

  const saveAudit = () => {
    const myAuditPayload = convertPayloadToMyAudit(
      activeTabContent!,
      startDate,
      dateTime,
      startDateRange,
      endDateRange
    );
    setIsSaveLoading(true);
    saveMyAudit(auditId, myAuditPayload)
      .then(() => {
        showSuccessMessage(t('COMMON.MY_AUDIT.CONDUCT.MESSAGES.SAVE.SUCCESS'));
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          alertMessage: t('COMMON.MY_AUDIT.CONDUCT.MESSAGES.SAVE.ERROR'),
          method: 'saveAudit',
          payload: myAuditPayload,
        });
        showErrorMessage(t('COMMON.MY_AUDIT.CONDUCT.MESSAGES.SAVE.ERROR'));
      })
      .finally(() => {
        setIsSaveLoading(false);
      });
  };

  const getAllStandardsAreNotGraded = () => {
    const scores = [1, 0, null];
    const allStandardsMissingScore: IStandard[] = [];
    if (activeTabContent) {
      activeTabContent.myAuditSections.map((section) => {
        section.standards.map((standard) => {
          if (!scores.includes(standard.score as ScoreTypes)) {
            allStandardsMissingScore.push(standard);
          }
        });
      });
    }
    return allStandardsMissingScore;
  };

  const setAllStandardsNotGradedToNA = () => {
    const scores = [1, 0, null];
    const updatedActiveTabContent = { ...activeTabContent! };
    if (updatedActiveTabContent) {
      updatedActiveTabContent.myAuditSections.map((section) => {
        section.standards.map((standard) => {
          if (!scores.includes(standard.score as ScoreTypes)) {
            standard.score = null;
          }
        });
      });
    }
    setActiveTabContent(updatedActiveTabContent);
  };

  const submitAudit = () => {
    setIsSubmitLoading(true);
    setAllStandardsNotGradedToNA();
    const myAuditPayload = convertPayloadToMyAudit(
      activeTabContent!,
      startDate,
      dateTime,
      startDateRange,
      endDateRange
    );
    submitMyAudit(auditId, myAuditPayload)
      .then(() => {
        showSuccessMessage(
          t('COMMON.MY_AUDIT.CONDUCT.MESSAGES.SUBMIT.SUCCESS')
        );
        navigate('/myaudit?filterBy=received');
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          alertMessage: t('COMMON.MY_AUDIT.CONDUCT.MESSAGES.SUBMIT.ERROR'),
          method: 'submitAudit',
          payload: myAuditPayload,
        });
        showErrorMessage(t('COMMON.MY_AUDIT.CONDUCT.MESSAGES.SUBMIT.ERROR'));
      })
      .finally(() => {
        setIsSubmitLoading(false);
        setIsShowConfirmationSubmitModal(false);
      });
  };

  const handleSubmitModal = () => {
    setStandardsMissingGrading([]);
    const allStandardsAreNotGraded = getAllStandardsAreNotGraded();
    setIsShowConfirmationSubmitModal(true);
    if (allStandardsAreNotGraded.length > 0) {
      setStandardsMissingGrading(allStandardsAreNotGraded);
      return;
    }
    setIsShowConfirmationSubmitModal(true);
  };

  const handleModalClose = () => {
    setIsShowConfirmationSubmitModal(false);
  };

  function handleTaggedUsers (taggedUsers: ISelectedOption[]) {
    const updatedTabContent: IMyAudit = { ...activeTabContent! };
    if (updatedTabContent) {
      updatedTabContent.myAuditSections[selectedTabIndex].tagged =
        taggedUsers as ISelectedOption[];
    }
  }

  const tabs =
    activeTabContent &&
    activeTabContent.myAuditSections.map((section) => {
      return {
        title: section.name,
        content: (
          <MyAuditConductTabContent
            section={section}
            handleMediaUpload={handleMediaUpload}
            onClickScore={handleScoreChanges}
            onDelete={handleDeleteImage}
            handleQuestionComment={handleQuestionComment}
            handleReplaceImage={handleReplaceImage}
            questionImgLoading={questionImgLoading}
            handleTaggedUsers={handleTaggedUsers}
            handleMyAuditComments={handleMyAuditComments}
            handleRoomNumber={handleRoomNumber}
            handleOutletName={handleOutletName}
            handleMealPeriod={handleMealPeriod}
            startDate={startDate}
            setStartDate={setStartDate}
            dateTime={dateTime}
            setDateTime={setDateTime}
            startDateRange={startDateRange}
            setStartDateRange={setStartDateRange}
            endDateRange={endDateRange}
            setEndDateRange={setEndDateRange}
            isLoadingImg={isLoadingImg}
            isLoadingImgDelete={isLoadingImgDelete}
          />
        ),
      };
    });

  return (
    <>
      <HeaderContainer>
        <AppHeader
          title={t('COMMON.MY_AUDIT.TITLE')}
          navigateTo="/myaudit?filterBy=received"
        />
      </HeaderContainer>
      <StyledMyAuditConductContainer>
        {isLoading ? (
          <ConductSkeleton />
        ) : (
          <>
            <ScoreContainer>
              <Score scoreList={scoreList} />
            </ScoreContainer>
            <TabsWrapper>
              <TabContainer
                title={activeTabContent && activeTabContent.templateName}
                tabs={tabs}
                handleTabSelection={setSelectedTabIndex}
                selectedTabIndex={selectedTabIndex}
              />
            </TabsWrapper>
            <MyAuditConductButtonContainer>
              <ButtonBlockColumn>
                <ButtonLarge
                  title={t('COMMON.MY_AUDIT.BUTTON.SAVE_MY_AUDIT')}
                  onClick={() => {
                    const isDateRangeValid = validateMyAuditDateRange(
                      activeTabContent!,
                      startDateRange,
                      endDateRange
                    );
                    if (isDateRangeValid) {
                      showErrorMessage(
                        t(
                          'COMMON.ALERT_MESSAGES.PLEASE_SELECT_VALID_DATE_RANGE'
                        )
                      );
                      return;
                    }
                    saveAudit();
                  }}
                  backgroundColor="oxfordBlue"
                  loading={isSaveLoading}
                />
                {!isSaveLoading && (
                  <>
                    <ButtonLarge
                      title={t('COMMON.MY_AUDIT.BUTTON.SUBMIT_MY_AUDIT')}
                      onClick={() => {
                        const isDateRangeValid = validateMyAuditDateRange(
                          activeTabContent!,
                          startDateRange,
                          endDateRange
                        );
                        if (isDateRangeValid) {
                          showErrorMessage(
                            t(
                              'COMMON.ALERT_MESSAGES.PLEASE_SELECT_VALID_DATE_RANGE'
                            )
                          );
                          return;
                        }
                        handleSubmitModal();
                      }}
                      backgroundColor="verdigris"
                      textColor="oxfordBlue"
                    />
                    <ButtonLarge
                      onClick={() => navigate('/myaudit?filterBy=received')}
                      title="Back"
                      outlined={true}
                      inverted={true}
                    />
                  </>
                )}
              </ButtonBlockColumn>
            </MyAuditConductButtonContainer>
          </>
        )}
      </StyledMyAuditConductContainer>
      {isShowConfirmationSubmitModal && (
        <Modal
          isOpen={isShowConfirmationSubmitModal}
          handleModalClose={handleModalClose}
        >
          {isSubmitLoading && <MyAuditConductModalSkeleton />}
          {!isSubmitLoading && (
            <>
              <ModalHeader>
                <Headline3>
                  {t('COMMON.MY_AUDIT.CONDUCT.MESSAGES.SUBMIT.CONFIRMATION')}
                </Headline3>
              </ModalHeader>
              <ModalContent>
                {standardsMissingGrading &&
                standardsMissingGrading.length > 0 ? (
                  <Body4>
                    {t(
                      'COMMON.MY_AUDIT.CONDUCT.MESSAGES.SUBMIT.CONFIRMATION_MESSAGE_MISSING_GRADING'
                    )}
                  </Body4>
                ) : (
                  <Body4>
                    {t(
                      'COMMON.MY_AUDIT.CONDUCT.MESSAGES.SUBMIT.CONFIRMATION_MESSAGE'
                    )}
                  </Body4>
                )}
              </ModalContent>
              <ButtonBlockColumn>
                <ButtonLarge title="Submit myAudit" onClick={submitAudit} />
                <ButtonLarge
                  title="Continue Editing"
                  onClick={handleModalClose}
                  outlined={true}
                  inverted={true}
                />
              </ButtonBlockColumn>
            </>
          )}
        </Modal>
      )}
    </>
  );
}
